import { App } from "@/constants";
import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import {
    growaveConfigurationToken,
    gwStorefrontAppInfoToken,
    modalManagerToken,
} from "@/tokens";
import checkIsMobile from "@/utils/checkIsMobile";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { MinilogLogger } from "@lib/Logger";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { SDK_MODULES } from "@modules/sdk/constants";
import { SdkModule } from "@modules/sdk/SdkModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { filter, take, tap } from "rxjs";
import { assert } from "superstruct";
import { container, inject, instanceCachingFactory, singleton } from "tsyringe";

import { ApplicationInterface } from "@interfaces/ApplicationInterface";

import {
    AverageReviewsWidgetDefinition,
    ListingAverageReviewsWidgetDefinition,
    ProductReviewsMiniSliderWidgetDefinition,
    QuestionsFormModalWidgetDefinition,
    QuestionsListWidgetDefinition,
    ReviewsDrawerWidgetDefinition,
    ReviewsFormModalWidgetDefinition,
    ReviewsGalleryWidgetDefinition,
    ReviewsListHeaderWidgetDefinition,
    ReviewsListWidgetDefinition,
    ReviewsPageWidgetDefinition,
    ReviewsProductPageWidgetDefinition,
    ReviewsSliderWidgetDefinition,
    ReviewsTrustBadgeWidgetDefinition,
    ReviewsWidgetDefinition,
} from "./definitions";
import ReviewsStorefrontValuesBrandingEntity from "./entities/ReviewsStorefrontValuesBrandingEntity";
import ReviewsSdk from "./reviews_sdk/ReviewsSdk";
import {
    ReviewsApplicationConfigInterface,
    ReviewsApplicationConfigSchema,
} from "./ReviewsApplicationConfig";
import ReviewsStorefrontValuesService from "./services/ReviewsStorefrontValuesService";
import {
    ReviewsApplicationConfigToken,
    questionsFormModalToken,
    reviewsApplicationLoggerToken,
    reviewsAuthApiClientToken,
    reviewsFormModalToken,
    reviewsIconCollectionManagerToken,
    reviewsTranslationsAuthApiClientToken,
} from "./tokens";
import { REWIEWS_BRANDING_LAUNCHER_VISIBILITY_TYPES } from "./types";
import ReviewsIconCollectionManager from "./utils/ReviewsIconCollectionManager";
import AverageReviewsWidgetInjector from "./widgets/average_reviews_widget/AverageReviewsWidgetInjector";
import ListingAverageReviewsWidgetInjector from "./widgets/listing_average_reviews_widget/ListingAverageReviewsWidgetInjector";
import ProductReviewsMiniSliderWidgetInjector from "./widgets/product_reviews_mini_slider_widget/ProductReviewsMiniSliderWidgetInjector";
import QuestionsFormModalWidgetInjector from "./widgets/questions_form_modal_widget/QuestionsFormModalWidgetInjector";
import QuestionsListWidgetInjector from "./widgets/questions_list_widget/QuestionsListWidgetInjector";
import ReviewsFormModalWidgetInjector from "./widgets/review_form_modal_widget/ReviewsFormModalWidgetInjector";
import ReviewsDrawerWidgetInjector from "./widgets/reviews_drawer_widget/ReviewsDrawerWidgetInjector";
import ReviewsGalleryWidgetInjector from "./widgets/reviews_gallery_widget/ReviewsGalleryWidgetInjector";
import ReviewsListHeaderWidgetInjector from "./widgets/reviews_list_header_widget/ReviewsListHeaderWidgetInjector";
import ReviewsListWidgetInjector from "./widgets/reviews_list_widget/ReviewsListWidgetInjector";
import ReviewsPageWidgetInjector from "./widgets/reviews_page_widget/ReviewsPageWidgetInjector";
import ReviewsProductPageWidgetInjector from "./widgets/reviews_product_page_widget/ReviewsProductPageWidgetInjector";
import ReviewsSliderWidgetInjector from "./widgets/reviews_slider_widget/ReviewsSliderWidgetInjector";
import ReviewsTrustBadgeWidgetInjector from "./widgets/reviews_trust_badge_widget/ReviewsTrustBadgeWidgetInjector";
import ReviewsWidgetInjector from "./widgets/reviews_widget/ReviewsWidgetInjector";

@singleton()
export class ReviewsApplication implements ApplicationInterface<object> {
    widgets = [];
    exports = {};

    constructor(
        @inject(ReviewsApplicationConfigToken)
        public readonly config: ReviewsApplicationConfigInterface,
        @inject(CommonModule)
        private readonly commonModule: CommonModule,
        @inject(BaseModule)
        public readonly baseModule: BaseModule,
        @inject(DynamicImportModule)
        private readonly dynamicImportModule: DynamicImportModule,
        @inject(MutationObserverInjector)
        private readonly mutationObserverInjector: MutationObserverInjector
    ) {
        assert(config, ReviewsApplicationConfigSchema);

        const dynamicImportManager =
            this.dynamicImportModule.exports.dynamicImportManager;

        dynamicImportManager.register(AverageReviewsWidgetDefinition);
        dynamicImportManager.register(ListingAverageReviewsWidgetDefinition);
        dynamicImportManager.register(ProductReviewsMiniSliderWidgetDefinition);
        dynamicImportManager.register(QuestionsFormModalWidgetDefinition);
        dynamicImportManager.register(ReviewsFormModalWidgetDefinition);
        dynamicImportManager.register(ReviewsDrawerWidgetDefinition);
        dynamicImportManager.register(ReviewsGalleryWidgetDefinition);
        dynamicImportManager.register(ReviewsPageWidgetDefinition);
        dynamicImportManager.register(ReviewsProductPageWidgetDefinition);
        dynamicImportManager.register(ReviewsSliderWidgetDefinition);
        dynamicImportManager.register(ReviewsWidgetDefinition);
        dynamicImportManager.register(ReviewsListWidgetDefinition);
        dynamicImportManager.register(QuestionsListWidgetDefinition);
        dynamicImportManager.register(ReviewsListHeaderWidgetDefinition);
        dynamicImportManager.register(ReviewsTrustBadgeWidgetDefinition);
    }

    init(): this {
        // Design Mode
        const isDesignMode =
            this.commonModule.exports.designModeProvider.isDesignMode() ===
            true;
        const analyticEventsCollectorModule = container.resolve(
            AnalyticsEventsCollectorModule
        );
        const reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );
        const reviewsBranding$ =
            reviewsStorefrontValuesService.selectReviewsBrandingOptions();
        const logger = new MinilogLogger("reviews");
        container.registerInstance(reviewsApplicationLoggerToken, logger);

        container.register(reviewsAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/reviews-storefront/v2"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                designMode: isDesignMode || false,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(reviewsApplicationLoggerToken)
                        ),
                        analyticEventsCollectorModule.exports.eventsCollector,
                        this.commonModule.exports.locationController,
                        analyticEventsCollectorModule.exports.eventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        container.register(reviewsTranslationsAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/content-translation-service/v2"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(reviewsApplicationLoggerToken)
                        ),
                        analyticEventsCollectorModule.exports.eventsCollector,
                        this.commonModule.exports.locationController,
                        analyticEventsCollectorModule.exports.eventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        container.register(reviewsIconCollectionManagerToken, {
            useFactory: instanceCachingFactory((di) => {
                return new ReviewsIconCollectionManager(
                    reviewsBranding$,
                    di.resolve(DynamicImportModule).exports.dynamicImportManager
                );
            }),
        });

        // Register Reviews SDK module
        const reviewsSdk = new ReviewsSdk(
            container.resolve(SdkModule).exports.sdkMethodsCollector
        );
        container
            .resolve(SdkModule)
            .exports.sdk.registerModule(SDK_MODULES.REVIEWS, reviewsSdk);

        logger.debug("ReviewsApplication is initialized", {
            ...this.config,
        });

        const locationController = this.commonModule.exports.locationController;
        const path = locationController.getPathname();

        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const isReviewsAppEnabled =
            growaveConfiguration.availableApps.includes(App.Reviews) ||
            isDesignMode;
        const isQuestionsAppEnabled =
            growaveConfiguration.availableApps.includes(App.Questions) ||
            isDesignMode;

        const modalManager = container.resolve(modalManagerToken);
        modalManager.addModal(reviewsFormModalToken);
        modalManager.addModal(questionsFormModalToken);

        // Injectors
        const reviewsWidgetInjector = new ReviewsWidgetInjector(
            this.config.reviewsWidgetPlaceholder,
            logger,
            analyticEventsCollectorModule.exports.eventsCollector,
            analyticEventsCollectorModule.exports.eventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        const reviewsDrawerWidgetInjector = new ReviewsDrawerWidgetInjector(
            this.config.drawerWidgetPlaceholderSelector,
            logger,
            analyticEventsCollectorModule.exports.eventsCollector,
            analyticEventsCollectorModule.exports.eventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        const averageReviewsWidgetInjector = new AverageReviewsWidgetInjector(
            this.config.averageWidgetPlaceholderSelector,
            logger,
            analyticEventsCollectorModule.exports.eventsCollector,
            analyticEventsCollectorModule.exports.eventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        const listingAverageReviewsInjector =
            new ListingAverageReviewsWidgetInjector(
                this.config.listingAverageReviewsWidgetPlaceholderSelector,
                logger,
                analyticEventsCollectorModule.exports.eventsCollector,
                analyticEventsCollectorModule.exports.eventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const reviewsProductPageWidgetInjector =
            new ReviewsProductPageWidgetInjector(
                this.config.reviewsProductPageWidgetPlaceholderSelector,
                logger,
                analyticEventsCollectorModule.exports.eventsCollector,
                analyticEventsCollectorModule.exports.eventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const reviewsPageWidgetInjector = new ReviewsPageWidgetInjector(
            this.config.reviewsPageWidgetPlaceholderSelector,
            logger,
            analyticEventsCollectorModule.exports.eventsCollector,
            analyticEventsCollectorModule.exports.eventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        const reviewsSliderWidgetInjector = new ReviewsSliderWidgetInjector(
            this.config.reviewsSliderWidgetPlaceholderSelector,
            logger,
            analyticEventsCollectorModule.exports.eventsCollector,
            analyticEventsCollectorModule.exports.eventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        const reviewsListHeaderWidgetInjector =
            new ReviewsListHeaderWidgetInjector(
                this.config.reviewsListHeaderWidgetPlaceholderSelector,
                logger,
                analyticEventsCollectorModule.exports.eventsCollector,
                analyticEventsCollectorModule.exports.eventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const reviewsListWidgetInjector = new ReviewsListWidgetInjector(
            this.config.reviewsListWidgetPlaceholderSelector,
            logger,
            analyticEventsCollectorModule.exports.eventsCollector,
            analyticEventsCollectorModule.exports.eventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        const questionsListWidgetInjector = new QuestionsListWidgetInjector(
            this.config.questionsListWidgetPlaceholderSelector,
            logger,
            analyticEventsCollectorModule.exports.eventsCollector,
            analyticEventsCollectorModule.exports.eventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        const reviewsGalleryWidgetInjector = new ReviewsGalleryWidgetInjector(
            this.config.reviewsGalleryWidgetPlaceholderSelector,
            logger,
            analyticEventsCollectorModule.exports.eventsCollector,
            analyticEventsCollectorModule.exports.eventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        const productReviewsMiniSliderWidgetInjector =
            new ProductReviewsMiniSliderWidgetInjector(
                this.config.productReviewsMiniSliderWidgetPlaceholderSelector,
                logger,
                analyticEventsCollectorModule.exports.eventsCollector,
                analyticEventsCollectorModule.exports.eventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const reviewsFormModalWidgetInjector =
            new ReviewsFormModalWidgetInjector(
                this.config.reviewsFormModalWidgetPlaceholderSelector,
                logger,
                analyticEventsCollectorModule.exports.eventsCollector,
                analyticEventsCollectorModule.exports.eventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const questionsFormModalWidgetInjector =
            new QuestionsFormModalWidgetInjector(
                this.config.questionsFormModalWidgetPlaceholderSelector,
                logger,
                analyticEventsCollectorModule.exports.eventsCollector,
                analyticEventsCollectorModule.exports.eventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const reviewsTrustBadgeWidgetInjector =
            new ReviewsTrustBadgeWidgetInjector(
                this.config.reviewsTrustBadgeWidgetPlaceholderSelector,
                logger,
                analyticEventsCollectorModule.exports.eventsCollector,
                analyticEventsCollectorModule.exports.eventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        reviewsWidgetInjector.start();

        if (!path.includes("/pages/reviews") && isReviewsAppEnabled) {
            reviewsBranding$
                .pipe(
                    filter(
                        (
                            branding
                        ): branding is ReviewsStorefrontValuesBrandingEntity =>
                            !!branding
                    ),
                    take(1),
                    tap((reviewsBranding) => {
                        const isMobile = checkIsMobile();
                        const settings = reviewsBranding.reviewsDrawerSettings;

                        const isDrawerVisible = (() => {
                            switch (settings.device) {
                                case REWIEWS_BRANDING_LAUNCHER_VISIBILITY_TYPES.allDevices:
                                    return true;
                                case REWIEWS_BRANDING_LAUNCHER_VISIBILITY_TYPES.desktopOnly:
                                    return !isMobile;
                                case REWIEWS_BRANDING_LAUNCHER_VISIBILITY_TYPES.hide:
                                default:
                                    return false;
                            }
                        })();

                        if (isDrawerVisible) {
                            reviewsDrawerWidgetInjector.start();
                        }
                    })
                )
                .subscribe();
        }

        const modulesStatusesProvider =
            this.baseModule.exports.modulesStatusesProvider;

        if (isReviewsAppEnabled) {
            listingAverageReviewsInjector.start();
            reviewsPageWidgetInjector.start();
            reviewsSliderWidgetInjector.start();
            reviewsGalleryWidgetInjector.start();
            productReviewsMiniSliderWidgetInjector.start();
            reviewsFormModalWidgetInjector.start();
            reviewsListHeaderWidgetInjector.start();
            reviewsListWidgetInjector.start();
            questionsListWidgetInjector.start();
            reviewsTrustBadgeWidgetInjector.start();

            modulesStatusesProvider.updateModuleStatus(
                App.Reviews,
                MODULE_STATUS.INITIALIZED
            );

            // Dynamic import ReviewsFormModalWidget
            modalManager
                .selectIsOpen(reviewsFormModalToken)
                .pipe(
                    filter((isOpen) => isOpen),
                    tap(() => {
                        void this.dynamicImportModule.exports.dynamicImportManager.fetch(
                            {
                                definition: ReviewsFormModalWidgetDefinition,
                            }
                        );
                    }),
                    take(1)
                )
                .subscribe();
        }

        if (isReviewsAppEnabled || isQuestionsAppEnabled) {
            reviewsProductPageWidgetInjector.start();
            averageReviewsWidgetInjector.start();
        }

        if (isQuestionsAppEnabled) {
            questionsFormModalWidgetInjector.start();

            modulesStatusesProvider.updateModuleStatus(
                App.Questions,
                MODULE_STATUS.INITIALIZED
            );

            // Dynamic import QuestionsFormModalWidget
            modalManager
                .selectIsOpen(questionsFormModalToken)
                .pipe(
                    filter((isOpen) => isOpen),
                    tap(() => {
                        void this.dynamicImportModule.exports.dynamicImportManager.fetch(
                            {
                                definition: QuestionsFormModalWidgetDefinition,
                            }
                        );
                    }),
                    take(1)
                )
                .subscribe();
        }

        return this;
    }
}
